import {useCallback, useEffect, useRef} from "react";
import {useImages} from "contexts/images";
import {useSetting} from "contexts/setting";
import {useUrlBackground} from "hooks/useBackgroundNumbers";
import {useCurrentSet} from "hooks/useCurrentSet";
import {composeSceneBgTransforms} from "libs/bgTransform";
import {backgroundAppearanceOf, type BackgroundAppearance} from "libs/backgroundAppearance";
import {bakedBgStateForDefault} from "libs/bakedBg";
import {buildBgPresets} from "layout/BgTransformer/templateBg";
import type {EffectiveTransform} from "contexts/bgTransform";

/** Apply the background without loading the scene's content or layout. */
export function useApplyBackgroundAppearance() {
  const {
    editTransform,
    loadCustomBgs,
    loadCustomBgUploaded,
    loadBgPresets,
    loadBakedBgs,
    loadBgOpacities,
    loadBgBrightnesses,
    loadLogoVariants,
    loadTextColorLocked,
    loadButtonColors,
    editBgBrightness,
    editBgOpacity,
    editBgBlurValue,
  }: any = useImages();
  const {editBase, editBases}: any = useSetting();
  return useCallback(
    (appearance: BackgroundAppearance) => {
      editTransform(
        appearance.bgX,
        appearance.bgY,
        appearance.bgScale,
        appearance.bgRotate,
        appearance.bgFlip
      );
      loadCustomBgs(appearance.customBgs);
      loadCustomBgUploaded(appearance.customBgUploaded);
      loadBgPresets(appearance.bgPresets);
      loadBakedBgs(appearance.bakedBgs, appearance.bakedBgOption);
      loadBgOpacities(appearance.bgOpacities);
      loadBgBrightnesses(appearance.bgBrightnesses);
      loadLogoVariants(appearance.logoVariants);
      loadTextColorLocked(appearance.textColorLocked);
      loadButtonColors(appearance.buttonColors);
      editBgBrightness(appearance.bgBrightness);
      editBgOpacity(appearance.bgOpacity);
      editBgBlurValue(appearance.bgBlurValue);
      editBase(appearance.base);
      editBases(appearance.bases);
    },
    [
      editTransform,
      loadCustomBgs,
      loadCustomBgUploaded,
      loadBgPresets,
      loadBakedBgs,
      loadBgOpacities,
      loadBgBrightnesses,
      loadLogoVariants,
      loadTextColorLocked,
      loadButtonColors,
      editBgBrightness,
      editBgOpacity,
      editBgBlurValue,
      editBase,
      editBases,
    ]
  );
}

interface Options {
  enabled: boolean;
  sceneId: string | undefined;
  getTileDefaults: (variation: string) =>
    | {
        scene: Record<string, unknown> | null;
        base: string;
        imageUrl: string | undefined;
      }
    | undefined;
  getTileTransform: (id: string, variation: string) => EffectiveTransform | null | undefined;
  tileError: unknown;
}

/**
 * A saved link starts with its saved appearance. A background in the URL is
 * an explicit gallery selection and receives that background's full default.
 * Read the same defaults as the tiles. Apply once per selection, so a query
 * refresh cannot replace later edits. No text or layout fields are loaded.
 */
export function useSceneBackgroundSelection({
  enabled,
  sceneId,
  getTileDefaults,
  getTileTransform,
  tileError,
}: Options) {
  const {background, isPending: isNumberPending} = useUrlBackground();
  const setting: any = useSetting();
  const {
    dataChannel,
    dataType,
    product,
    layoutOption,
    variantOption,
    visible,
    editBgOption,
    editPinnedSceneBg,
  } = setting;
  const set = useCurrentSet();
  const apply = useApplyBackgroundAppearance();
  const active = Boolean(
    enabled && background && Number.isInteger(Number(background)) && Number(background) > 0
  );
  const key = active
    ? JSON.stringify([
        sceneId,
        dataChannel,
        dataType,
        product.value,
        set,
        layoutOption,
        variantOption,
        background,
      ])
    : null;
  const applied = useRef<string | null>(null);
  useEffect(() => {
    if (!key || !background) {
      applied.current = null;
      return;
    }
    if (applied.current === key || tileError) return;
    const defaults = getTileDefaults(background);
    if (defaults === undefined) return;
    const {scene, base, imageUrl} = defaults;
    // Use the same banner list as buildScene, including other layouts.
    const transforms = composeSceneBgTransforms(visible, (id: string) =>
      getTileTransform(id, background)
    );
    const baked = bakedBgStateForDefault({scene, resolvedFor: background, displayed: background});
    const appearance = backgroundAppearanceOf({
      ...scene,
      ...transforms,
      base,
      bgPresets: buildBgPresets(scene ?? {}),
      bakedBgs: baked.bakedBgs,
      bakedBgOption: baked.option,
    });
    apply(appearance);
    editBgOption(background);
    // A numbered pick uses the current catalog image, including on a reload.
    editPinnedSceneBg(
      imageUrl
        ? {sceneId, variation: Number(background), url: imageUrl, product: product.value}
        : null
    );
    applied.current = key;
  }, [
    key,
    background,
    getTileDefaults,
    getTileTransform,
    tileError,
    visible,
    apply,
    editBgOption,
    editPinnedSceneBg,
    sceneId,
    product.value,
  ]);

  return {
    active,
    isPending:
      Boolean(enabled && isNumberPending) || Boolean(key && applied.current !== key && !tileError),
    isError: active && Boolean(tileError),
    error: tileError,
  };
}
